import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import * as Switch from '@radix-ui/react-switch';

import MoonStroke from '@/assets/icons/moon_stroke.svg?component';
import SunStroke from '@/assets/icons/sun_stroke.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useThemeActions, useThemeState } from '@/contexts/theme';

import { AnglerfishIcon } from './AnglerfishIcon';
import { useOccasion } from './useOccasion';

import styles from './ThemeToggle.module.scss';

export type ThemeToggleVariant = 'header' | 'mobile';

export interface ThemeToggleProps {
    variant?: ThemeToggleVariant;
}

/** Pill switch between the light and dark theme. Renders nothing for users without the flag. */
export default function ThemeToggle(props: ThemeToggleProps) {
    const { variant = 'header' } = props;
    const { t } = useTranslation();
    const { theme, canToggle } = useThemeState();
    const { setTheme } = useThemeActions();
    const occasion = useOccasion();
    // useId returns ':r1:'-style ids; colons need escaping in CSS selectors, so drop them.
    const id = `theme-toggle-${useId().replace(/:/g, '')}`;

    if (!canToggle) return null;

    const isDark = theme === 'dark';
    const title = isDark
        ? t('atoms.themeToggle.switchToLight')
        : t('atoms.themeToggle.switchToDark');

    const toggle = (
        <Switch.Root
            id={id}
            className={styles.root}
            data-variant={variant}
            data-theme-toggle=""
            checked={isDark}
            onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
            aria-label={variant === 'mobile' ? undefined : t('atoms.themeToggle.label')}
            title={title}>
            <span className={`${styles.trackIcon} ${styles.sun}`}>
                <SvgIcon icon={SunStroke} svgProps={{ 'aria-hidden': true }} />
            </span>
            <span className={`${styles.trackIcon} ${styles.moon}`}>
                <SvgIcon icon={MoonStroke} svgProps={{ 'aria-hidden': true }} />
            </span>
            <Switch.Thumb className={styles.thumb}>
                <AnglerfishIcon occasion={occasion} />
            </Switch.Thumb>
        </Switch.Root>
    );

    if (variant !== 'mobile') return toggle;

    // In the mobile sidebar footer every control is a full-width labelled row.
    return (
        <div className={styles.mobileRow}>
            <label htmlFor={id} className={styles.mobileLabel}>
                {t('atoms.themeToggle.label')}
            </label>
            {toggle}
        </div>
    );
}
